import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    Icon,
    Box,
    Grid
} from '@material-ui/core';

/**
 * VolumeDetailsDialog - A reusable component for displaying and editing volume details
 * across different cloud providers (AWS, Azure, GCP, OCI, MAAS)
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Function} props.onSave - Function to save volume changes
 * @param {Object} props.volume - The volume object to display/edit
 * @param {Object} props.storageConfig - The storage configuration object
 * @param {boolean} props.isEditable - Whether the volume can be edited
 * @param {number} props.nodeGroupIndex - The index of the node group (optional)
 */
const VolumeDetailsDialog = ({
    open,
    onClose,
    onSave,
    volume,
    storageConfig,
    isEditable = false,
    nodeGroupIndex = null
}) => {
    const [editedData, setEditedData] = useState({
        size: volume?.size || 0,
        mountPath: volume?.mountPath || '/mnt/unknown',
        policyName: volume?.policyName || 'default',
        performanceOverrides: {
            readIops: volume?.performanceOverrides?.readIops || storageConfig?.performanceConfig?.readIops || '',
            writeIops: volume?.performanceOverrides?.writeIops || storageConfig?.performanceConfig?.writeIops || '',
            readBandwidth: volume?.performanceOverrides?.readBandwidth || storageConfig?.performanceConfig?.readBandwidth || '',
            writeBandwidth: volume?.performanceOverrides?.writeBandwidth || storageConfig?.performanceConfig?.writeBandwidth || ''
        },
        resiliencyConfig: {
            mediaFailures: volume?.resiliencyConfig?.mediaFailures || 0,
            nodeFailures: volume?.resiliencyConfig?.nodeFailures || 0,
            zoneFailures: volume?.resiliencyConfig?.zoneFailures || 0
        }
    });

    // Update edited data when volume changes
    React.useEffect(() => {
        if (volume) {
            setEditedData({
                size: volume.size || 0,
                mountPath: volume.mountPath || '/mnt/unknown',
                policyName: volume.policyName || 'default',
                performanceOverrides: {
                    readIops: volume.performanceOverrides?.readIops || storageConfig?.performanceConfig?.readIops || '',
                    writeIops: volume.performanceOverrides?.writeIops || storageConfig?.performanceConfig?.writeIops || '',
                    readBandwidth: volume.performanceOverrides?.readBandwidth || storageConfig?.performanceConfig?.readBandwidth || '',
                    writeBandwidth: volume.performanceOverrides?.writeBandwidth || storageConfig?.performanceConfig?.writeBandwidth || ''
                },
                resiliencyConfig: {
                    mediaFailures: volume.resiliencyConfig?.mediaFailures || 0,
                    nodeFailures: volume.resiliencyConfig?.nodeFailures || 0,
                    zoneFailures: volume.resiliencyConfig?.zoneFailures || 0
                }
            });
        }
    }, [volume, storageConfig]);

    const handleDataChange = (field, value) => {
        setEditedData(prev => {
            const keys = field.split('.');
            if (keys.length === 1) {
                return { ...prev, [field]: value };
            } else if (keys.length === 2) {
                return {
                    ...prev,
                    [keys[0]]: {
                        ...prev[keys[0]],
                        [keys[1]]: value
                    }
                };
            }
            return prev;
        });
    };

    const handleSave = () => {
        if (onSave) {
            const updatedVolume = {
                ...volume,
                size: parseInt(editedData.size) || 0,
                mountPath: editedData.mountPath,
                policyName: editedData.policyName,
                performanceOverrides: {
                    readIops: editedData.performanceOverrides.readIops ? parseInt(editedData.performanceOverrides.readIops) : undefined,
                    writeIops: editedData.performanceOverrides.writeIops ? parseInt(editedData.performanceOverrides.writeIops) : undefined,
                    readBandwidth: editedData.performanceOverrides.readBandwidth ? parseInt(editedData.performanceOverrides.readBandwidth) : undefined,
                    writeBandwidth: editedData.performanceOverrides.writeBandwidth ? parseInt(editedData.performanceOverrides.writeBandwidth) : undefined
                },
                resiliencyConfig: {
                    mediaFailures: parseInt(editedData.resiliencyConfig.mediaFailures) || 0,
                    nodeFailures: parseInt(editedData.resiliencyConfig.nodeFailures) || 0,
                    zoneFailures: parseInt(editedData.resiliencyConfig.zoneFailures) || 0
                }
            };
            onSave(updatedVolume, nodeGroupIndex);
        }
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    if (!volume) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="volume-details-dialog-title"
        >
            <DialogTitle id="volume-details-dialog-title">
                {isEditable ? 'Volume Configuration' : 'Volume Details'}
                {storageConfig && (
                    <Typography variant="subtitle2" color="textSecondary">
                        {storageConfig.providerName}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <div>
                    {/* General */}
                    <Typography variant="subtitle1" style={{ marginBottom: '16px', marginTop: '8px' }}>
                        General
                    </Typography>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <TextField
                            label="Size (GB)"
                            type="number"
                            variant="outlined"
                            value={editedData.size || ''}
                            onChange={(e) => handleDataChange('size', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                        />
                        <TextField
                            label="Mount Path"
                            variant="outlined"
                            value={editedData.mountPath || ''}
                            onChange={(e) => handleDataChange('mountPath', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                        />
                        <TextField
                            label="Policy Name"
                            variant="outlined"
                            value={editedData.policyName || ''}
                            onChange={(e) => handleDataChange('policyName', e.target.value)}
                            disabled={true}
                            fullWidth
                        />
                    </div>

                    {/* Performance */}
                    <Typography variant="subtitle1" style={{ marginBottom: '16px' }}>
                        Performance
                    </Typography>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <TextField
                            label="Read IOPS"
                            type="number"
                            variant="outlined"
                            value={editedData.performanceOverrides?.readIops || ''}
                            onChange={(e) => handleDataChange('performanceOverrides.readIops', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                            helperText="Leave empty to use storage config default"
                        />
                        <TextField
                            label="Write IOPS"
                            type="number"
                            variant="outlined"
                            value={editedData.performanceOverrides?.writeIops || ''}
                            onChange={(e) => handleDataChange('performanceOverrides.writeIops', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                            helperText="Leave empty to use storage config default"
                        />
                        <TextField
                            label="Read Bandwidth (MB/s)"
                            type="number"
                            variant="outlined"
                            value={editedData.performanceOverrides?.readBandwidth || ''}
                            onChange={(e) => handleDataChange('performanceOverrides.readBandwidth', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                            helperText="Leave empty to use storage config default"
                        />
                        <TextField
                            label="Write Bandwidth (MB/s)"
                            type="number"
                            variant="outlined"
                            value={editedData.performanceOverrides?.writeBandwidth || ''}
                            onChange={(e) => handleDataChange('performanceOverrides.writeBandwidth', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                            helperText="Leave empty to use storage config default"
                        />
                    </div>

                    {/* Resiliency */}
                    <Typography variant="subtitle1" style={{ marginBottom: '16px' }}>
                        Resiliency
                    </Typography>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <TextField
                            label="Media Failures"
                            type="number"
                            variant="outlined"
                            value={editedData.resiliencyConfig?.mediaFailures || 0}
                            onChange={(e) => handleDataChange('resiliencyConfig.mediaFailures', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                        />
                        <TextField
                            label="Node Failures"
                            type="number"
                            variant="outlined"
                            value={editedData.resiliencyConfig?.nodeFailures || 0}
                            onChange={(e) => handleDataChange('resiliencyConfig.nodeFailures', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                        />
                        <TextField
                            label="Zone Failures"
                            type="number"
                            variant="outlined"
                            value={editedData.resiliencyConfig?.zoneFailures || 0}
                            onChange={(e) => handleDataChange('resiliencyConfig.zoneFailures', e.target.value)}
                            disabled={!isEditable || editedData.policyName !== 'custom'}
                            fullWidth
                        />
                    </div>
                </div>
                {isEditable && editedData.policyName !== 'custom' && (
                    <Typography 
                        variant="body2" 
                        color="textSecondary" 
                        style={{ 
                            marginTop: '16px', 
                            fontStyle: 'italic',
                            backgroundColor: '#fffbf0',
                            border: '1px solid #ffeaa7',
                            borderRadius: '4px',
                            padding: '12px 16px'
                        }}
                    >
                        Note: Only volumes with custom policies can be edited. Volumes using predefined policies are read-only.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent="flex-end" spacing={2}>
                    <Grid item>
                        <Button onClick={handleClose} color="secondary">
                            Cancel
                        </Button>
                    </Grid>
                    {isEditable && (
                        <Grid item>
                            <Button 
                                onClick={handleSave} 
                                color="primary" 
                                variant="contained"
                                disabled={editedData.policyName !== 'custom'}
                            >
                                Save Changes
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default VolumeDetailsDialog;
